import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface Supplier {
  id: string;
  pin: string;
  name: string;
  email: string;
  phoneNumber: string;
  deleted: boolean;
  editedTime: string | null;
  createdTime: string;
  isBanned: boolean;
  companyName: string;
  registrationNumber: string;
  okpo: string;
  inn: string;
  registrationDate: string;
  legalStatus: {
    id: number;
    nameRu: string;
    nameKy: string;
  } | null;
  supplierType: string;
  passportNumber: string;
  applicantTypeEnum: string;
  actingAs: string | null;
  contractNumber: string | null;
  contractDate: string | null;
  externalUserId: string;
}

export interface SupplierResponse {
  success: boolean;
  msg: string;
  res: Supplier | null;
  time: string;
  ver: string;
}

export interface SupplierListResponse {
  success: boolean;
  msg: string | null;
  res: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    content: Supplier[];
  };
  time: string;
  ver: string;
}

@Injectable({
  providedIn: 'root'
})
export class SupplierService {
  private http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  // Сигнал для хранения статуса регистрации
  private isRegisteredSignal = signal<boolean | null>(null);
  private supplierDataSignal = signal<Supplier | null>(null);

  // Публичные сигналы
  isRegistered = this.isRegisteredSignal.asReadonly();
  supplierData = this.supplierDataSignal.asReadonly();

  /**
   * Получить данные поставщика
   */
  getSupplierInfo(): Observable<SupplierResponse> {
    return this.http
      .get<SupplierResponse>(`${this.apiUrl}/tariff-quota/api/user/external`)
      .pipe(
        tap(response => {
          if (response.success && response.res) {
            this.isRegisteredSignal.set(true);
            this.supplierDataSignal.set(response.res);
          } else {
            this.isRegisteredSignal.set(false);
            this.supplierDataSignal.set(null);
          }
        }),
        catchError(err => {
          console.error('Ошибка получения данных поставщика:', err);
          // Если 404 или ошибка - значит не зарегистрирован
          this.isRegisteredSignal.set(false);
          this.supplierDataSignal.set(null);
          return of({
            success: false,
            msg: 'Поставщик не найден',
            res: null,
            time: new Date().toISOString(),
            ver: '1.0'
          });
        })
      );
  }

  /**
   * Проверить зарегистрирован ли поставщик
   */
  checkRegistrationStatus(): Observable<boolean> {
    return new Observable(observer => {
      this.getSupplierInfo().subscribe({
        next: (response) => {
          const isRegistered = response.success && response.res !== null;
          observer.next(isRegistered);
          observer.complete();
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Сбросить кеш (например после регистрации)
   */
  clearCache(): void {
    this.isRegisteredSignal.set(null);
    this.supplierDataSignal.set(null);
  }

  /**
   * Зарегистрировать внешнего пользователя (POST /user/external)
   */
  registerExternalUser(body: { consentPersonalData: boolean; passportNumber: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/tariff-quota/api/user/external`, body);
  }

  /**
   * Получить список компаний текущего пользователя
   */
  getMySuppliers(page = 1, size = 15): Observable<SupplierListResponse> {
    return this.http.post<SupplierListResponse>(
      `${this.apiUrl}/tariff-quota/api/suppliers/get-all`,
      { page, size }
    );
  }

  /**
   * Удалить или восстановить компанию
   */
  updateSupplierAction(supplierId: string, action: 'DELETE' | 'RESTORE', reason: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/tariff-quota/api/suppliers`, {
      supplierId,
      action,
      reason
    });
  }

  /**
   * Установить статус регистрации (после успешной регистрации)
   */
  setRegistered(supplier: Supplier): void {
    this.isRegisteredSignal.set(true);
    this.supplierDataSignal.set(supplier);
  }
}
